/**
 * Het advertentielogboek van één woning (MA-22): eerst gezien, prijzen, weg,
 * terug. Eén regel per gebeurtenis, oudste boven, met de prijs van dat moment
 * rechts - zo lees je een geschiedenis.
 *
 * Plus de korte vorm die bij het adres staat: "eerder actief geweest", hoe vaak
 * en sinds wanneer. Die staat er zodra de woning het weet, ook zonder dat het
 * logboek zelf is opengeklapt.
 */
import {
  type Aanbod,
  formatDatum,
  formatEuro,
  type Logboek as LogboekData,
  type LogboekGebeurtenis,
} from '@mamimap/shared';
import { RefreshCw, RotateCcw } from 'lucide-react';

/** "Eerder actief geweest · 2x terug · sinds 3 sep 2026". */
export function eerderActiefTekst(terugkomsten: number, sinds?: string | null): string {
  const delen = ['Eerder actief geweest'];
  if (terugkomsten > 1) delen.push(`${terugkomsten}× terug`);
  if (sinds) delen.push(`sinds ${formatDatum(sinds)}`);
  return delen.join(' · ');
}

/** De markering bij het adres; niets als hij nooit weg is geweest. */
export function EerderActief({
  terugkomsten,
  sinds,
  compact = false,
}: {
  terugkomsten: number;
  sinds?: string | null;
  compact?: boolean;
}) {
  if (terugkomsten <= 0) return null;
  const tekst = eerderActiefTekst(terugkomsten, sinds);
  if (compact) {
    return (
      <span title={tekst} className="inline-flex items-center text-accent">
        <RotateCcw className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="sr-only">{tekst}</span>
      </span>
    );
  }
  return (
    <p className="mt-1 flex items-start gap-1 text-[11.5px] leading-snug text-accent-deep">
      <RotateCcw className="mt-[2px] h-3 w-3 shrink-0" aria-hidden="true" />
      {tekst}
    </p>
  );
}

/**
 * "Opnieuw aangeboden · was € 435.000 (−8%)" (MA-45): de makelaar zette dezelfde
 * woning met een nieuwe advertentie terug. Het verschil in procenten, want dat
 * is het getal waar je op let; zonder vorige prijs alleen de eerste woorden.
 */
export function opnieuwAangebodenTekst(vraagprijs: number, vorige: number | null): string {
  if (vorige === null || vorige <= 0) return 'Opnieuw aangeboden';
  const verschil = Math.round(((vraagprijs - vorige) / vorige) * 100);
  const procent =
    verschil === 0 ? 'zelfde prijs' : `${verschil > 0 ? '+' : '−'}${Math.abs(verschil)}%`;
  return `Opnieuw aangeboden · was ${formatEuro(vorige)} (${procent})`;
}

/** De markering bij het adres; niets als dit de eerste advertentie is. */
export function OpnieuwAangeboden({
  opnieuwAangeboden,
  vraagprijs,
  vorigeVraagprijs,
  compact = false,
}: {
  opnieuwAangeboden: boolean;
  vraagprijs: number;
  vorigeVraagprijs: number | null;
  compact?: boolean;
}) {
  if (!opnieuwAangeboden) return null;
  const tekst = opnieuwAangebodenTekst(vraagprijs, vorigeVraagprijs);
  if (compact) {
    return (
      <span title={tekst} className="inline-flex items-center text-brick">
        <RefreshCw className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="sr-only">{tekst}</span>
      </span>
    );
  }
  return (
    <p className="mt-1 flex items-start gap-1 text-[11.5px] leading-snug text-brick">
      <RefreshCw className="mt-[2px] h-3 w-3 shrink-0" aria-hidden="true" />
      {tekst}
    </p>
  );
}

const AANBOD_REGEL: Record<Exclude<Aanbod, 'actief'>, string> = {
  verkocht: 'Verkocht',
  verdwenen: 'Uit het aanbod',
};

/** "Verkocht" of "Uit het aanbod sinds 1 okt"; niets zolang hij te koop staat. */
export function AanbodRegel({ aanbod, sinds }: { aanbod: Aanbod; sinds?: string | null }) {
  if (aanbod === 'actief') return null;
  return (
    <p className="mt-1 text-[11.5px] text-brick">
      {AANBOD_REGEL[aanbod]}
      {sinds ? ` sinds ${formatDatum(sinds)}` : ''}
    </p>
  );
}

function omschrijving(gebeurtenis: LogboekGebeurtenis): string {
  switch (gebeurtenis.soort) {
    case 'eerste_waarneming':
      return 'Eerst gezien';
    case 'prijs': {
      const { vraagprijs, vorigePrijs } = gebeurtenis;
      if (vraagprijs === null || vorigePrijs === null) return 'Prijs gewijzigd';
      return vraagprijs < vorigePrijs
        ? `Prijs omlaag van ${formatEuro(vorigePrijs)}`
        : `Prijs omhoog van ${formatEuro(vorigePrijs)}`;
    }
    case 'merkje':
      return gebeurtenis.merkje ?? 'Merkje eraf';
    case 'verdwenen':
      return 'Uit het aanbod';
    case 'teruggekomen':
      return 'Terug in het aanbod';
  }
}

export function Logboek({
  logboek,
  bezig,
  fout,
}: {
  logboek: LogboekData | undefined;
  bezig: boolean;
  fout: unknown;
}) {
  return (
    <section aria-label="Logboek">
      {fout ? (
        <p className="text-[11.5px] text-brick">Het logboek kon niet geladen worden.</p>
      ) : bezig || !logboek ? (
        <p className="text-[11.5px] text-faint">Laden</p>
      ) : (
        <ol className="border-t border-line">
          {logboek.gebeurtenissen.map((gebeurtenis) => (
            <li
              // Twee gebeurtenissen in dezelfde haal delen hun tijdstip, maar
              // nooit ook hun soort: per haal is er hooguit één van elk.
              key={`${gebeurtenis.op}-${gebeurtenis.soort}`}
              data-soort={gebeurtenis.soort}
              className="grid grid-cols-[74px_1fr_auto] items-baseline gap-2 border-b border-line py-[3px] text-[11.5px]"
            >
              <span className="cijfers text-muted">{formatDatum(gebeurtenis.op)}</span>
              <span
                className={
                  gebeurtenis.soort === 'verdwenen'
                    ? 'text-brick'
                    : gebeurtenis.soort === 'teruggekomen'
                      ? 'text-accent-deep'
                      : 'text-ink-soft'
                }
              >
                {omschrijving(gebeurtenis)}
              </span>
              <span className="cijfers text-right text-ink">
                {gebeurtenis.vraagprijs === null ? '–' : formatEuro(gebeurtenis.vraagprijs)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
